import { PrimaryButton } from "components/designV2/buttons/primaryButton";
import { Heading } from "components/designV2/heading/heading";
import { SecondaryButton } from "components/designV2/buttons/secondaryButton";
import { SearchableDropdown } from "components/designV2/searchableDropdown/searchableDropdown";
import { CustomIcon } from "components/icon/customIcon.component";
import { useTranslation } from "react-i18next";
import { GroupDivider } from "web-apps/company/components/common/shared/groupDivider.component";
import { UnitTeamDropdown } from "web-apps/company/components/common/filters/unitTeamDropdown.component";
import { AddStaffPanel } from "./components/addStaffPanel/addStaffPanel.component";
import { StaffOptionsPanel } from "./components/optionsPanel/staffOptionsPanel.component";
import { StaffProfilePanel } from "./components/profilePanel/profilePanel.component";
import { StaffListRow } from "./components/staffListRow.component";
import {
  ControlsRow,
  Description,
  HeaderRow,
  HeadingBlock,
  Layout,
  LinkIconSlot,
  ListMessage,
  ListRows,
  ListSection,
  ListWrapper,
  LoadMoreSentinel,
  OrganizationLink,
  OptionsSlot,
} from "./staff.styled";
import { useStaffLogic } from "./staff.logic";

export const Staff = (): JSX.Element => {
  const { t } = useTranslation();
  const logic = useStaffLogic();

  return (
    <Layout>
      <HeaderRow>
        <HeadingBlock>
          <Heading level="h2">{t("Staff.Staff")}</Heading>
          <Description>
            {t("Staff.ManageDescription")}{" "}
            <OrganizationLink to="/company/organizationalStructure">
              {t("Staff.Organization")}
              <LinkIconSlot>
                <CustomIcon name="v2/right-chevron" size="14px" />
              </LinkIconSlot>
            </OrganizationLink>
          </Description>
        </HeadingBlock>
        <PrimaryButton
          label={t("Staff.AddStaff")}
          iconName="v2/plus"
          onClick={() => logic.setIsAddStaffPanelOpen(true)}
        />
      </HeaderRow>

      <ControlsRow>
        <UnitTeamDropdown
          label={t("Staff.UnitTeam")}
          placeholder={t("Staff.AllUnitsTeamsVisible")}
          emptyText={
            logic.filterOptions.unitTeamLoadError
              ? t("OrganizationalStructure.UnitTeamLoadFailed")
              : t("OrganizationalStructure.NoUnitTeamMatches")
          }
          options={logic.filterOptions.unitTeamOptions}
          selectedTeamIds={logic.selectedWorkGroupIds}
          onSelectedTeamIdsChange={logic.setSelectedWorkGroupIds}
        />
        <SearchableDropdown
          label={t("Staff.Person")}
          placeholder={t("Staff.SearchOrBrowse")}
          emptyText={
            logic.filterOptions.workersLoadError
              ? t("OrganizationalStructure.WorkersLoadFailed")
              : t("OrganizationalStructure.NoPersonMatches")
          }
          options={logic.filterOptions.personOptions}
          leadingIconName="v2/search"
          multiSelect
          selectedValues={logic.selectedPersonIds}
          onSelectedValuesChange={logic.setSelectedPersonIds}
          onClear={() => logic.setSelectedPersonIds([])}
        />
        <OptionsSlot>
          <SecondaryButton
            label={t("Staff.Options")}
            size="small"
            iconName="v2/settings"
            onClick={() => logic.setIsOptionsOpen(true)}
          />
        </OptionsSlot>
      </ControlsRow>

      <StaffOptionsPanel
        isOpen={logic.isOptionsOpen}
        groupByOption={logic.groupByOption}
        onGroupByChange={logic.setGroupByOption}
        onClose={() => logic.setIsOptionsOpen(false)}
      />
      <AddStaffPanel
        isOpen={logic.isAddStaffPanelOpen}
        onClose={() => logic.setIsAddStaffPanelOpen(false)}
        unitTeamOptions={logic.filterOptions.unitTeamOptions}
        onWorkerCreated={logic.refreshStaff}
      />
      <StaffProfilePanel
        companyId={logic.companyId ?? null}
        workerId={logic.selectedWorkerId}
        unitTeamOptions={logic.filterOptions.unitTeamOptions}
        isOpen={logic.selectedWorkerId != null}
        onClose={logic.closeWorkerProfile}
        onWorkerDeleted={logic.handleWorkerDeleted}
      />

      <ListWrapper>
        {logic.isLoadingStaff ? (
          <ListMessage>{t("Staff.Loading")}</ListMessage>
        ) : logic.staffLoadError ? (
          <ListMessage>{t("Staff.LoadError")}</ListMessage>
        ) : logic.isEmptyState ? (
          <ListMessage>{t("Staff.NoResults")}</ListMessage>
        ) : (
          <>
            {logic.sections.map((section) => (
              <ListSection key={section.id}>
                {logic.groupByOption === "team" &&
                  section.unitName &&
                  section.teamName && (
                    <GroupDivider
                      unitName={section.unitName}
                      teamName={`${section.teamName} (${section.items.length})`}
                    />
                  )}
                <ListRows>
                  {section.items.map((item) => (
                    <StaffListRow
                      key={`${section.id}-${item.workerId}`}
                      item={item}
                      onSelectWorker={logic.openWorkerProfile}
                    />
                  ))}
                </ListRows>
              </ListSection>
            ))}
            {logic.isLoadingMoreStaff && (
              <ListMessage>{t("Staff.Loading")}</ListMessage>
            )}
            {logic.hasMoreStaff && <LoadMoreSentinel ref={logic.loadMoreRef} />}
          </>
        )}
      </ListWrapper>
    </Layout>
  );
};
